import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input';
import { FeeService } from '../service/fee.service';
import { BulkMembersRequest, FeeGroupResponse, FeeSubgroupResponse, MemberCreateRequest } from '../interface/fee.interface';
import { FrequencyType } from '../enums/FrequencyType';
import { DiscountType } from '../enums/DiscountType';
import { FeeStateService } from '../service/fee-state.service';
import { TranslateService } from '@ngx-translate/core';
import { PHONE_NUMBER_PATTERN } from '../constants/validation.constants';
import { handleDiscountKeyDown, handleDiscountReasonKeyDown, integerValidator, sanitizeDiscountPaste, sanitizeDiscountReasonPaste, syncDiscountControlErrors } from '../utils/fee-discount.utils';

interface NgxPhoneValue {
  number?: string;
}

function readPhoneNumber(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return (value as NgxPhoneValue).number ?? '';
}

function phoneRequiredPatternValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const num = readPhoneNumber(control.value).trim();
    if (!num) return { required: true };
    return PHONE_NUMBER_PATTERN.test(num) ? null : { pattern: true };
  };
}

export interface AddMemberDialogData {
  industryId: number;
  preselectedGroupId?: number;
  preselectedSubgroupId?: number;
}

interface MemberMeta {
  saved: boolean;
  expanded: boolean;
}

function alphanumericValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    // Latin, Armenian (U+0531–U+058A, U+FB13–U+FB17), digits, hyphens, spaces
    return /^[A-Za-z0-9Ա-֊ﬓ-ﬗ -]+$/.test(control.value) ? null : { alphanumeric: true };
  };
}

@Component({
  selector: 'app-fee-add-member-dialog',
  templateUrl: './fee-add-member-dialog.component.html',
  styleUrls: ['./fee-add-member-dialog.component.scss']
})
export class FeeAddMemberDialogComponent implements OnInit, OnDestroy {

  private readonly destroy$ = new Subject<void>();

  readonly defaultCountryISO = CountryISO.Armenia;
  readonly preferredCountries: CountryISO[] = [CountryISO.Armenia];
  readonly phoneNumberFormat = PhoneNumberFormat.International;

  memberForms!: FormArray;
  groups: FeeGroupResponse[] = [];
  filteredSubgroupsByIndex: FeeSubgroupResponse[][] = [];
  memberMetas: MemberMeta[] = [];
  submitting = false;
  loadingGroups = true;
  duplicateSubgroupErrors: boolean[] = [];
  invalidPhoneMessages: (string | null)[] = [];
  private industryId = 0;
  private preselectedGroupId?: number;
  private preselectedSubgroupId?: number;
  private returnUrl: string | null = null;

  readonly frequencyTypes = Object.values(FrequencyType);
  readonly discountTypes = Object.values(DiscountType);

  private discountTypeBeforeOpen = '';

  submitAttemptedByIndex: boolean[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly feeService: FeeService,
    private readonly feeStateService: FeeStateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    @Optional() private readonly dialogRef: MatDialogRef<FeeAddMemberDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) readonly data: AddMemberDialogData | null
  ) {}

  ngOnInit(): void {
    this.memberForms = this.fb.array([]);
    this.resolveContext();

    if (!this.industryId) {
      this.loadingGroups = false;
      return;
    }

    this.feeService.getUserIndustryById(this.industryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(userIndustry => {
        this.feeStateService.setActiveUserIndustry(userIndustry);
      });

    this.feeService.getFeeGroups(this.industryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(groups => {
        this.groups = groups;
        this.loadingGroups = false;
        this.addMemberCard();
      });
  }

  ngOnDestroy(): void {
    this.feeStateService.setActiveUserIndustry(null);
    this.destroy$.next();
    this.destroy$.complete();
  }

  isPhoneEmpty(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    const numberField = (value as { number?: string }).number;
    return !numberField || numberField.trim() === '';
  }

  private getFormattedPhone(index: number): string | undefined {
    const raw = this.getMemberControl(index, 'phone').value;
    if (this.isPhoneEmpty(raw)) return undefined;
    if (typeof raw === 'string') return raw;
    const phone = raw as { e164Number?: string; internationalNumber?: string; number?: string };
    return phone.e164Number || phone.internationalNumber || phone.number || undefined;
  }

  hasPhoneError(index: number): boolean {
    return this.getMemberControl(index, 'phone').invalid;
  }

  isPhoneVisuallyInvalid(index: number): boolean {
    const ctrl = this.getMemberControl(index, 'phone');
    return ctrl.touched && this.hasPhoneError(index);
  }

  validatePhoneNumber(index: number): void {
    const ctrl = this.getMemberControl(index, 'phone');
    if (ctrl.valid) {
      this.invalidPhoneMessages[index] = null;
      return;
    }
    if (ctrl.hasError('required')) {
      this.invalidPhoneMessages[index] = 'fee.validation.phone_required';
    } else {
      this.invalidPhoneMessages[index] = 'registration_content.valid_phone_number';
    }
  }

  getSelectedGroupName(index: number): string {
    const groupId = this.getMemberControl(index, 'groupId').value;
    if (!groupId) return '—';
    return this.groups.find(group => group.id === groupId)?.name || '—';
  }

  getSelectedSubgroupName(index: number): string {
    const subgroupId = this.getMemberControl(index, 'subgroupId').value;
    if (!subgroupId) return '—';
    return this.filteredSubgroupsByIndex[index]?.find(subgroup => subgroup.id === subgroupId)?.name || '—';
  }

  get memberFormControls(): FormGroup[] {
    return this.memberForms.controls as FormGroup[];
  }

  get atLeastOneMemberSaved(): boolean {
    return this.memberMetas.some(m => m.saved);
  }

  getMemberForm(index: number): FormGroup {
    return this.memberForms.at(index) as FormGroup;
  }

  getMemberControl(index: number, controlName: string): AbstractControl {
    return this.getMemberForm(index).get(controlName)!;
  }

  private buildForm(groupId?: number, subgroupId?: number): FormGroup {
    return this.fb.group({
      groupId:     [groupId ?? '', Validators.required],
      subgroupId:  [subgroupId ?? '', Validators.required],
      name:        ['', [Validators.minLength(2), Validators.maxLength(50)]],
      frequency:   [{ value: '', disabled: true }, Validators.required],
      passportId:  ['', [Validators.maxLength(20), alphanumericValidator()]],
      dateOfBirth: [''],
      phone:       [undefined, [phoneRequiredPatternValidator()]],
      email:       ['', [Validators.required, Validators.email]],
      discountType:  [''],
      discountValue: [null, [Validators.min(0), integerValidator()]],
      discountReason: ['', Validators.maxLength(100)],
    });
  }

  private addMemberCard(groupId?: number, subgroupId?: number): void {
    const effectiveGroupId = groupId ?? this.preselectedGroupId;
    const effectiveSubgroupId = subgroupId ?? this.preselectedSubgroupId;
    const form = this.buildForm(effectiveGroupId, effectiveSubgroupId);
    const index = this.memberForms.length;
    this.memberForms.push(form);
    form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.syncMemberDiscount(form));
    this.submitAttemptedByIndex[index] = false;
    this.duplicateSubgroupErrors[index] = false;
    this.memberMetas[index] = { saved: false, expanded: true };
    this.invalidPhoneMessages[index] = null;
    this.filteredSubgroupsByIndex[index] = this.resolveFilteredSubgroups(effectiveGroupId);
    if (effectiveSubgroupId) {
      this.applySubgroupFrequency(index, effectiveSubgroupId);
      this.syncMemberDiscountReason(index);
    }
    this.cdr.detectChanges();
  }

  toggleMemberCard(index: number): void {
    this.memberMetas[index].expanded = !this.memberMetas[index].expanded;
  }

  private resolveContext(): void {
    const paramIndustryId = Number(this.route.snapshot.paramMap.get('id'));
    const queryGroupId = Number(this.route.snapshot.queryParamMap.get('groupId'));
    const querySubgroupId = Number(this.route.snapshot.queryParamMap.get('subgroupId'));
    const queryReturnUrl = this.route.snapshot.queryParamMap.get('returnUrl');

    this.industryId = this.data?.industryId ?? (Number.isNaN(paramIndustryId) ? 0 : paramIndustryId);
    this.preselectedGroupId = this.data?.preselectedGroupId
      ?? (Number.isNaN(queryGroupId) ? undefined : queryGroupId);
    this.preselectedSubgroupId = this.data?.preselectedSubgroupId
      ?? (Number.isNaN(querySubgroupId) ? undefined : querySubgroupId);
    this.returnUrl = queryReturnUrl && queryReturnUrl.startsWith('/') ? queryReturnUrl : null;
  }

  onGroupChange(index: number, groupId: number | ''): void {
    this.duplicateSubgroupErrors[index] = false;
    const form = this.getMemberForm(index);
    if (!groupId) {
      this.filteredSubgroupsByIndex[index] = this.resolveFilteredSubgroups();
      form.get('subgroupId')!.setValue('', { emitEvent: false });
      this.applySubgroupFrequency(index, '');
      this.syncMemberDiscountReason(index);
      return;
    }
    this.filteredSubgroupsByIndex[index] = this.resolveFilteredSubgroups(groupId);
    const currentSubgroupId = form.get('subgroupId')!.value;
    if (currentSubgroupId && !this.filteredSubgroupsByIndex[index].some(sg => sg.id === currentSubgroupId)) {
      form.get('subgroupId')!.setValue('', { emitEvent: false });
    }
    this.applySubgroupFrequency(index, form.get('subgroupId')!.value || '');
  }

  onSubgroupChange(index: number, subgroupId: number | ''): void {
    this.applySubgroupFrequency(index, subgroupId);
    this.syncMemberDiscountReason(index);
    if (!subgroupId) return;
    const form = this.getMemberForm(index);
    const currentGroupId = form.get('groupId')!.value;
    if (currentGroupId) return;

    const allSubgroups = this.groups.flatMap(g => g.subgroups);
    const sg = allSubgroups.find(s => s.id === subgroupId);
    if (!sg) return;

    const matchingGroups = this.groups.filter(g => g.subgroups.some(s => s.name === sg.name));
    if (matchingGroups.length > 1) {
      this.duplicateSubgroupErrors[index] = true;
      return;
    }
    this.duplicateSubgroupErrors[index] = false;
    if (matchingGroups.length === 1) {
      form.get('groupId')!.setValue(matchingGroups[0].id, { emitEvent: false });
      this.filteredSubgroupsByIndex[index] = matchingGroups[0].subgroups;
    }
  }

  onAddMemberClick(): void {
    const lastForm = this.memberForms.length > 0 ? this.getMemberForm(this.memberForms.length - 1) : null;
    const savedGroupId = lastForm?.get('groupId')?.value || '';
    const savedSubgroupId = lastForm?.get('subgroupId')?.value || '';
    this.addMemberCard(savedGroupId || undefined, savedSubgroupId || undefined);
  }

  saveMember(index: number): void {
    const form = this.getMemberForm(index);
    this.submitAttemptedByIndex[index] = true;
    form.markAllAsTouched();
    if (form.invalid || this.duplicateSubgroupErrors[index]) return;
    this.validatePhoneNumber(index);
    if (form.invalid || this.duplicateSubgroupErrors[index] || this.hasPhoneError(index)) return;

    const v = form.value;
    form.patchValue({
      phone: this.getFormattedPhone(index) ?? v.phone
    }, { emitEvent: false });
    this.memberMetas[index] = { saved: true, expanded: false };
  }

  removeMemberCard(index: number): void {
    this.spliceMemberCard(index);
    this.ensureMemberDraftForm();
  }

  private spliceMemberCard(index: number): void {
    this.memberForms.removeAt(index);
    this.filteredSubgroupsByIndex.splice(index, 1);
    this.duplicateSubgroupErrors.splice(index, 1);
    this.submitAttemptedByIndex.splice(index, 1);
    this.memberMetas.splice(index, 1);
    this.invalidPhoneMessages.splice(index, 1);
  }

  /** Reopen an empty member form when no saved member and no draft card remains. */
  private ensureMemberDraftForm(): void {
    if (this.memberForms.length === 0) {
      this.addMemberCard();
      return;
    }
    if (!this.atLeastOneMemberSaved && !this.memberMetas.some(m => !m.saved)) {
      this.addMemberCard();
    }
  }

  private applySubgroupFrequency(index: number, subgroupId: number | ''): void {
    const freqCtrl = this.getMemberControl(index, 'frequency');
    if (!subgroupId) {
      freqCtrl.setValue('', { emitEvent: false });
      return;
    }
    const subgroup = this.groups.flatMap(g => g.subgroups).find(s => s.id === subgroupId);
    freqCtrl.setValue(subgroup?.frequency ?? '', { emitEvent: false });
  }

  private findSubgroup(subgroupId: number | ''): FeeSubgroupResponse | undefined {
    if (!subgroupId) return undefined;
    return this.groups.flatMap(g => g.subgroups).find(s => s.id === subgroupId);
  }

  private syncMemberDiscount(form: FormGroup): void {
    const price = this.findSubgroup(form.get('subgroupId')!.value)?.price ?? 0;
    syncDiscountControlErrors(form.get('discountType')!, form.get('discountValue')!, price);
  }

  memberBasePrice(index: number): number {
    return this.findSubgroup(this.getMemberControl(index, 'subgroupId').value)?.price ?? 0;
  }

  memberFinalPrice(index: number): number {
    const price = this.memberBasePrice(index);
    const { type, value } = this.effectiveMemberDiscount(index);
    if (!value || !type) {
      return price;
    }
    if (type === DiscountType.PERCENT) {
      return price - (price * value / 100);
    }
    return price - value;
  }

  private effectiveMemberDiscount(index: number): { type: string; value: number } {
    const form = this.getMemberForm(index);
    const overrideValue = Number(form.get('discountValue')?.value) || 0;
    const overrideType = form.get('discountType')?.value;
    if (overrideValue > 0 && overrideType) {
      return { type: overrideType, value: overrideValue };
    }
    const subgroup = this.findSubgroup(form.get('subgroupId')?.value);
    return {
      type: subgroup?.discountType ?? '',
      value: subgroup?.discountValue ?? 0
    };
  }

  private syncMemberDiscountReason(index: number): void {
    const subgroup = this.findSubgroup(this.getMemberControl(index, 'subgroupId').value);
    this.getMemberControl(index, 'discountReason').setValue(subgroup?.reason ?? '', { emitEvent: false });
  }

  onMemberDiscountKeyDown(event: KeyboardEvent): void {
    handleDiscountKeyDown(event);
  }

  onMemberDiscountPaste(index: number, event: ClipboardEvent): void {
    this.getMemberControl(index, 'discountValue').setValue(sanitizeDiscountPaste(event));
  }

  onMemberDiscountReasonKeyDown(event: KeyboardEvent): void {
    handleDiscountReasonKeyDown(event);
  }

  onMemberDiscountReasonPaste(index: number, event: ClipboardEvent): void {
    this.getMemberControl(index, 'discountReason').setValue(sanitizeDiscountReasonPaste(event));
  }

  onMemberDiscountSelectOpened(index: number, opened: boolean): void {
    if (opened) {
      this.discountTypeBeforeOpen = this.getMemberControl(index, 'discountType').value || '';
    }
  }

  onMemberDiscountOptionClick(index: number, value: string): void {
    if (this.discountTypeBeforeOpen === value) {
      setTimeout(() => this.getMemberControl(index, 'discountType').setValue(''));
    }
  }

  private mapMemberPayload(form: FormGroup, index: number): MemberCreateRequest {
    const value = form.getRawValue();
    const hasDiscount = value.discountValue !== null
      && value.discountValue !== '' && value.discountValue !== undefined;
    return {
      subgroupId: value.subgroupId,
      name: value.name,
      frequency: value.frequency,
      passportId: value.passportId || undefined,
      dateOfBirth: value.dateOfBirth ? this.toIsoDate(value.dateOfBirth) : undefined,
      phone: this.getFormattedPhone(index),
      email: value.email || undefined,
      discountType: hasDiscount ? (value.discountType || undefined) : undefined,
      discountValue: hasDiscount ? Number(value.discountValue) : undefined,
      discountReason: hasDiscount && this.hasValue(value.discountReason)
        ? String(value.discountReason).trim()
        : undefined,
    };
  }

  private toIsoDate(value: Date | string): string {
    const d = new Date(value);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  private hasValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  }

  private isDraftEmpty(form: FormGroup): boolean {
    const value = form.getRawValue();
    return !this.hasValue(value.name)
      && !this.hasValue(value.frequency)
      && !this.hasValue(value.passportId)
      && !this.hasValue(value.dateOfBirth)
      && this.isPhoneEmpty(value.phone)
      && !this.hasValue(value.email)
      && !this.hasValue(value.discountValue)
  }

  save(): void {
    if (this.submitting || !this.atLeastOneMemberSaved) return;
    if (this.memberForms.length > 0) {
      const nonEmptyMemberIndices = this.memberFormControls
        .map((form, index) => ({ form, index }))
        .filter(({ form }) => !this.isDraftEmpty(form))
        .map(({ index }) => index);

      for (const i of nonEmptyMemberIndices) {
        this.submitAttemptedByIndex[i] = true;
        this.getMemberForm(i).markAllAsTouched();
        this.validatePhoneNumber(i);
      }

      const hasInvalidMember = nonEmptyMemberIndices
        .some(index =>
          this.getMemberForm(index).invalid
          || this.duplicateSubgroupErrors[index]
          || this.hasPhoneError(index)
        );

      if (hasInvalidMember) return;
    }

    this.submitting = true;
    const savedPayload: MemberCreateRequest[] = this.memberFormControls
      .map((form, index) => ({ form, index }))
      .filter(({ index }) => this.memberMetas[index]?.saved)
      .map(({ form, index }) => this.mapMemberPayload(form, index));
    const draftPayload: MemberCreateRequest[] = this.memberFormControls
      .map((form, index) => ({ form, index }))
      .filter(({ form, index }) => !this.memberMetas[index]?.saved && !this.isDraftEmpty(form))
      .map(({ form, index }) => this.mapMemberPayload(form, index));
    const members: MemberCreateRequest[] = [...savedPayload, ...draftPayload];
    if (members.length === 0) {
      this.submitting = false;
      return;
    }

    const bulkRequest: BulkMembersRequest = { members };
    this.feeService.bulkCreateMembers(bulkRequest)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next:  () => {
          if (this.dialogRef) {
            this.dialogRef.close(true);
            return;
          }
          void this.navigateBack();
        },
        error: () => { this.submitting = false; }
      });
  }

  cancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close(false);
      return;
    }
    void this.navigateBack();
  }

  private navigateBack(): Promise<boolean> {
    if (this.returnUrl) {
      return this.router.navigateByUrl(this.returnUrl);
    }
    if (this.preselectedGroupId) {
      return this.router.navigate(['/fees', this.industryId, 'groups', this.preselectedGroupId]);
    }
    return this.router.navigate(['/fees', this.industryId]);
  }

  frequencyLabel(freq: string): string {
    if (!freq) return '';
    const key = `fee.frequency_types.${freq.toUpperCase()}`;
    const translated = this.translateService.instant(key);
    return translated !== key ? translated : freq;
  }

  private resolveFilteredSubgroups(groupId?: number): FeeSubgroupResponse[] {
    if (!groupId) {
      return this.groups.flatMap(g => g.subgroups);
    }
    return this.groups.find(g => g.id === groupId)?.subgroups ?? [];
  }
}
